import { Accordion, AccordionItem, Button, Input, Link } from '@heroui/react'
import { Code, CornerDownRight, Folder, HelpCircle, Home, PlusIcon, Search } from 'lucide-react'
import { CgSpinner } from 'react-icons/cg'
import DroppableFolderItem from './FolderItem'

function FolderSidebar({
  folders,
  onCreateFolder,
  onSelectFolder,
  selectedFolderId,
  onMovePathway,
  onEditFolder,
  onDeleteFolder,
  isCreatingFolder,
  searchQuery,
  setSearchQuery,
}) {
  const renderFolders = (folderList, level = 0) => {
    return folderList.map((folder) => {
      const folderContent = (
        <span className="flex w-full items-center gap-1">
          {level > 0 && <CornerDownRight size={14} className="text-default-500" />}
          <DroppableFolderItem
            key={folder.id}
            folder={folder}
            onSelectFolder={onSelectFolder}
            selectedFolderId={selectedFolderId}
            onMovePathway={onMovePathway}
            onEditFolder={onEditFolder}
            onDeleteFolder={onDeleteFolder}
          />
        </span>
      )
      return (
        <div
          key={folder.id}
          className={`
            ml-${level * 2}
            py-1.5
          `}
        >
          {folderContent}
          {folder.children
            && Array.isArray(folder.children)
            && (renderFolders(
              folder.children.filter(child => child.type === 'folder'),
              level + 1,
            ))}
        </div>
      )
    })
  }

  const rootItems = Array.isArray(folders) ? folders : []

  return (
    <div className={`
      flex h-full max-h-full w-1/4 max-w-(--breakpoint-sm) min-w-80 flex-col
      justify-start gap-3 overflow-y-scroll border-r border-gray-200
      bg-gray-50 p-4
    `}
    >
      <Input
        placeholder="Search for Pathways"
        value={searchQuery}
        startContent={<Search size={16} />}
        variant="bordered"
        className="bg-white"
        radius="none"
        onChange={e => setSearchQuery(e.target.value)}
      />
      <Button
        onPress={() => onSelectFolder(null)}
        variant="bordered"
        color="default"
        radius="none"
        fullWidth
        className="justify-start bg-white font-medium"
        disableRipple
        disableAnimation
        size="md"
      >
        <Home className="mr-1" size={16} />
        All Pathways
      </Button>

      <Accordion
        defaultExpandedKeys={['folders']}
        variant="bordered"
        size="sm"
        className="rounded-none! bg-white"
        itemClasses={{ base: 'rounded-none! shadow-none', title: 'text-sm', content: 'flex flex-col pt-0', trigger: 'py-4 font-medium' }}
      >
        <AccordionItem key="folders" size="sm" title="Folders" startContent={<Folder size={16} />}>
          {renderFolders(rootItems)}
          <Button
            onPress={onCreateFolder}
            isDisabled={isCreatingFolder}
            variant="light"
            disableRipple
            color="default"
            radius="none"
            fullWidth
            size="sm"
            className="mt-2"
          >
            {isCreatingFolder
              ? (
                  <CgSpinner className="mr-1.5 animate-spin" size={16} />
                )
              : (
                  <PlusIcon className="mr-1.5" size={16} />
                )}
            {isCreatingFolder ? 'Creating...' : 'New Folder'}
          </Button>
        </AccordionItem>
      </Accordion>
      <span className="flex flex-col gap-0">
        <Button
          variant="light"
          as={Link}
          href="https://docs.bland.ai/tutorials/pathways"
          color="default"
          radius="none"
          fullWidth
          size="sm"
          showAnchorIcon
          className="text-default-500"
        >
          <HelpCircle size={12} />
          Get help with pathways
        </Button>
        <Button
          variant="light"
          as={Link}
          href="https://docs.bland.ai/api-v1/get/all_pathway"
          color="default"
          radius="none"
          fullWidth
          size="sm"
          showAnchorIcon
          className="text-default-500"
        >
          <Code size={12} />
          Developer docs
        </Button>
      </span>
    </div>
  )
}

export default FolderSidebar
